



































































































































































































import Vue from 'vue'
import { mapState } from 'vuex'
import { ApiResponse, Fee, ItemFile, Project, Requisition, RequisitionItem, Sale } from '@/interfaces'
import { stockStatuses } from '@/formHelpers'
import HighlightIcon from '@/components/Icons/HighlightIcon.vue'
import SignedIcon from '@/components/Icons/SignedIcon.vue'

interface Rubrique {
  children?: Rubrique[]
  label: string
  value: string
}

interface Badge {
  color: string
  value: string
}

interface DropdownAction {
  action: string
  item?: RequisitionItem
  index?: number
  renumberBy?: string
}

export default Vue.extend({
  components: { HighlightIcon, SignedIcon },
  props: {
    item: {
      type: Object as () => RequisitionItem,
      required: false,
      default: null,
    },
    project: {
      type: Object as () => Project,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    page: {
      type: String,
      required: false,
      default: '',
    },
    itemsLength: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  data() {
    return {
      stockStatuses,
      rubriques: [],
      selectedItems: [] as RequisitionItem[],
      lastCheckedIndex: -1,
      editableItemNumber: '',
      isSavingItemNumber: false,
      itemNumberInputError: '',
      isUploadingItemFiles: false,
      isDragActive: false,
    }
  },
  computed: {
    ...mapState(['user']),
  },
  watch: {
    item: {
      deep: true,
      immediate: true,
      handler(newVal) {
        this.$set(this.item, 'isChecked', newVal.isChecked)
        this.editableItemNumber = this.formatEditableItemNumber(newVal)
        this.itemNumberInputError = ''
      },
    },
    user(newVal) {
      this.rubriques = newVal.client.config.rubriques
    },
  },
  mounted() {
    if (this.user) {
      this.rubriques = this.user.client.config.rubriques
    }
  },
  methods: {
    isDropEnabledPage(): boolean {
      return this.page === 'requisitionItemList' || this.page === 'saleItemList'
    },
    handleDragOver(event: DragEvent) {
      if (!this.isDropEnabledPage() || this.isUploadingItemFiles) {
        return
      }

      const hasFiles = !!event.dataTransfer?.types?.includes('Files')
      this.isDragActive = hasFiles
    },
    handleDragLeave(event: DragEvent) {
      if (!this.isDropEnabledPage()) {
        return
      }

      const currentTarget = event.currentTarget as Node | null
      const relatedTarget = event.relatedTarget as Node | null

      if (!currentTarget || !relatedTarget || !currentTarget.contains(relatedTarget)) {
        this.isDragActive = false
      }
    },
    fileToResizedBase64(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          if (!reader.result) {
            reject(new Error('empty_file'))
            return
          }

          const canvas = document.createElement('canvas')
          const image = document.createElement('img')
          let context = canvas.getContext('2d')

          image.onload = () => {
            canvas.width = image.width
            canvas.height = image.height
            context?.drawImage(image, 0, 0, image.width, image.height)

            const maxWidth = 2048
            const maxHeight = 2048
            let width = image.width
            let height = image.height

            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width
                width = maxWidth
              }
            } else if (height > maxHeight) {
              width *= maxHeight / height
              height = maxHeight
            }

            context?.clearRect(0, 0, canvas.width, canvas.height)

            context = canvas.getContext('2d')
            canvas.width = width
            canvas.height = height
            context?.drawImage(image, 0, 0, width, height)

            resolve(canvas.toDataURL('image/jpeg', 0.75).toString().split(',')[1])
          }
          image.onerror = () => reject(new Error('resize_image_failed'))
          image.src = reader.result.toString()
        }
        reader.onerror = () => reject(reader.error ?? new Error('read_file_failed'))
      })
    },
    getNextImagePosition(): number {
      let maxPosition = 0

      const files = this.item?.requisitionItemFiles ?? []
      files.forEach((file: ItemFile) => {
        if (file.type === 'image' && file.deletedAt == null && Number.isFinite(file.position)) {
          maxPosition = Math.max(maxPosition, file.position)
        }
      })

      const thumbnailCount = this.item?.thumbnail?.count ?? 0
      const existingCount = typeof thumbnailCount === 'number' ? thumbnailCount : 0

      return Math.max(maxPosition, existingCount, 0) + 1
    },
    async dropItemFiles(event: DragEvent) {
      this.isDragActive = false

      if (!this.isDropEnabledPage() || this.isUploadingItemFiles) {
        return
      }

      const droppedFiles = event.dataTransfer?.files
      if (!droppedFiles || droppedFiles.length === 0) {
        return
      }

      const files = Array.from(droppedFiles).filter((file) => file.type.startsWith('image/'))
      if (files.length === 0) {
        this.$notify({
          type: 'warning',
          title: 'Attention',
          message: 'Déposez au moins une image valide.',
        })
        return
      }

      if (!this.item?.id) {
        this.$notify({
          type: 'error',
          title: 'Erreur',
          message: 'Lot introuvable pour le téléversement.',
        })
        return
      }

      this.isUploadingItemFiles = true
      try {
        let nextPosition = this.getNextImagePosition()

        for (const file of files) {
          const data = await this.fileToResizedBase64(file)
          await this.$api.post(`/requisitionitem/${this.item.id}/file`, {
            filename: file.name,
            position: nextPosition,
            data,
          })

          nextPosition++
        }

        const response = await this.$api.get(`/requisitionitem/${this.item.id}/refresh`)
        const apiResponse = response.data as ApiResponse

        this.$notify({
          type: 'success',
          title: 'Succès',
          message: `${files.length} image(s) ajoutée(s) au lot.`,
        })
        this.$emit('itemFilesUploaded', apiResponse.data as RequisitionItem)
      } catch (error: unknown) {
        const apiError = error as { response?: { data?: { message?: string } } }
        const message = apiError?.response?.data?.message ?? 'Impossible de téléverser les images sur ce lot.'
        this.$notify({
          type: 'error',
          title: 'Erreur',
          message,
        })
      } finally {
        this.isUploadingItemFiles = false
      }
    },
    formatEditableItemNumber(item: RequisitionItem | null | undefined): string {
      const itemNumber = item?.itemNumber
      const itemSubNumber = item?.itemSubNumber

      if (itemNumber === null || itemNumber === undefined || itemNumber === '') {
        return ''
      }

      if (itemSubNumber === null || itemSubNumber === undefined || itemSubNumber === '') {
        return `${itemNumber}`
      }

      return `${itemNumber}-${itemSubNumber}`
    },
    async saveItemNumberOnBlur() {
      const currentFullItemNumber = this.formatEditableItemNumber(this.item)
      const nextFullItemNumber = (this.editableItemNumber ?? '').trim()
      const itemNumberPattern = /^\d+(?:-\d+)?$/

      if (nextFullItemNumber === currentFullItemNumber) {
        return
      }

      if (nextFullItemNumber === '' || !itemNumberPattern.test(nextFullItemNumber)) {
        this.itemNumberInputError = 'Format invalide. Utiliser entier ou entier-entier.'
        this.editableItemNumber = currentFullItemNumber
        return
      }

      this.itemNumberInputError = ''

      const [parsedItemNumberStr, parsedItemSubNumberStr] = nextFullItemNumber.split('-')
      const parsedItemNumber = parseInt(parsedItemNumberStr, 10)
      const parsedItemSubNumber = parsedItemSubNumberStr ?? null

      const requisitionId = this.item?.requisition?.id
      const itemId = this.item?.id

      if (!requisitionId || !itemId) {
        this.editableItemNumber = currentFullItemNumber
        return
      }

      this.isSavingItemNumber = true
      try {
        const response = await this.$api.put(`/requisition/${requisitionId}/item/${itemId}`, {
          itemNumber: parsedItemNumber,
          itemSubNumber: parsedItemSubNumber,
        })

        const apiResponse = response.data as ApiResponse
        const updatedItem = apiResponse?.data?.updatedItem as RequisitionItem | undefined

        if (updatedItem) {
          this.$set(this.item, 'itemNumber', updatedItem.itemNumber ?? null)
          this.$set(this.item, 'itemSubNumber', updatedItem.itemSubNumber ?? null)
          this.$set(this.item, 'fullItemNumber', updatedItem.fullItemNumber ?? null)
          this.editableItemNumber = this.formatEditableItemNumber(updatedItem)
          this.$emit('itemNumberUpdated', updatedItem)
        } else {
          this.$set(this.item, 'itemNumber', parsedItemNumber)
          this.$set(this.item, 'itemSubNumber', parsedItemSubNumber)
          this.$set(
            this.item,
            'fullItemNumber',
            parsedItemSubNumber !== null ? `${parsedItemNumber}-${parsedItemSubNumber}` : `${parsedItemNumber}`
          )
          this.editableItemNumber =
            parsedItemSubNumber !== null ? `${parsedItemNumber}-${parsedItemSubNumber}` : `${parsedItemNumber}`
          this.$emit('itemNumberUpdated', this.item)
        }
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Erreur',
          message: "Impossible d'enregistrer le numéro de lot.",
        })
        this.editableItemNumber = currentFullItemNumber
      } finally {
        this.isSavingItemNumber = false
      }
    },
    formatCurrency(number: string | number | null | undefined): string {
      const parsedNumber = typeof number === 'string' ? Number(number) : number
      const safeNumber =
        parsedNumber === null || parsedNumber === undefined || Number.isNaN(parsedNumber) ? 0 : parsedNumber

      return Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(safeNumber)
    },
    toggleChecked(item: RequisitionItem, index: number, event: Event | null) {
      this.$emit('toggleCheck', item, index, event)
    },
    editRequisitionItem(item: RequisitionItem) {
      this.$router.push({
        name: 'requisition_item_edit',
        params: {
          itemId: item.id?.toString() ?? '',
        },
        query: {
          backTo: this.$route.name,
          backScrollTo: window.scrollY.toString(),
        },
      })
    },
    getSaleTitle(sale: Sale | null | undefined): string {
      let saleTitle = ''
      if (sale?.title) {
        saleTitle += ' ' + sale?.title.slice(0, 40)
      }
      if (sale?.startDate) {
        const date = Date.parse(sale?.startDate)
        if (date) {
          saleTitle += ' - ' + new Intl.DateTimeFormat('fr-FR').format(date)
        }
      }
      return saleTitle !== '' ? saleTitle : '-'
    },
    getRubriqueRec(rubriques: Rubrique[], str: string | null | undefined): string {
      if (!str) {
        return '-'
      }

      let rub = '-'
      rubriques.forEach((rubrique: Rubrique) => {
        if (rubrique.value == str) {
          rub = rubrique.label
        }
        if (rubrique.children && rub == '-') {
          rub = this.getRubriqueRec(rubrique.children, str)
        }
      })

      return rub
    },
    getEndPrice(item: RequisitionItem): string {
      return item.endPrice !== null && item.endPrice !== undefined ? this.formatCurrency(item.endPrice) : '-'
    },
    getReservePrice(item: RequisitionItem): string {
      const reservePrice = item.reservePrice !== null && item.reservePrice !== undefined ? this.formatCurrency(item.reservePrice) : '-'
      const reserveNetPrice =
        item.reserveNetPrice !== null && item.reserveNetPrice !== undefined ? this.formatCurrency(item.reserveNetPrice) : '-'

      return `${reservePrice} / ${reserveNetPrice}`
    },
    getEstimationPrice(item: RequisitionItem): string {
      if ((item.estimationLow === null || item.estimationLow === undefined) && (item.estimationHigh === null || item.estimationHigh === undefined)) {
        return '-'
      }

      const estimationLow = item.estimationLow !== null && item.estimationLow !== undefined ? this.formatCurrency(item.estimationLow) : '-'
      const estimationHigh =
        item.estimationHigh !== null && item.estimationHigh !== undefined ? this.formatCurrency(item.estimationHigh) : '-'

      return `${estimationLow} - ${estimationHigh}`
    },
    getFeeBadge(fee: Fee): Badge {
      const badge: Badge = {
        color: '',
        value: fee?.label ?? '-',
      }
      switch (fee.type) {
        case 'percent_buyer':
        case 'fixe_buyer':
          badge.color = '#CE60AC26'
          break

        case 'percent_seller':
        case 'fixe_seller':
        case 'frais':
        case 'vente':
          badge.color = '#4365D626'
          break

        case 'emolument':
        case 'autre':
        default:
          badge.color = '#4C4C8326'
          break
      }
      return badge
    },
    getFeeBadgeColor(fee: Fee) {
      let color

      switch (fee.type) {
        case 'percent_buyer':
        case 'fixe_buyer':
          color = '#CE60AC'
          break

        case 'percent_seller':
        case 'fixe_seller':
        case 'frais':
          color = '#4365D6'
          break

        case 'autre':
        case 'emolument':
        default:
          color = '#4C4C83'
          break
      }

      return {
        color,
        'border-color': color,
      }
    },
    getMediaList(item: RequisitionItem) {
      return item.galleryImages ?? []
    },
    cleanRequisitionItemFiles(files: ItemFile[]) {
      return files.filter(this.cleanDeletedFiles).sort(this.sortByPosition)
    },
    cleanDeletedFiles(file: ItemFile) {
      return file.deletedAt !== 'NULL'
    },
    sortByPosition(file1: ItemFile, file2: ItemFile) {
      if (file1.position > file2.position) {
        return 1
      }

      if (file1.position < file2.position) {
        return -1
      }

      return 0
    },
    getThumbnail(files: ItemFile[], type: string) {
      for (let index = 0; index < files.length; index++) {
        const element = files[index]
        if (element.type == type && element.deletedAt == null) {
          return element.urlThumb
        }
      }

      return null
    },
    getProjectInfo(requisition: Requisition) {
      let projectInfo = ''
      if (requisition.project?.generatedNumber) {
        projectInfo = `${requisition.project.generatedNumber}`
      }
      if (requisition.debiteur) {
        projectInfo = `${projectInfo} \n> ${requisition.debiteur.formName}`
      }

      return projectInfo
    },
    handleItemCommand(command: DropdownAction) {
      if (command.action == 'document') {
        this.$emit('document', command.index ?? null)
      }

      // Edit item
      if (command.action == 'edit' && command.item) {
        this.editRequisitionItem(command.item)
      }

      // Duplicate item
      if (command.action == 'duplicate' && command.item) {
        this.$emit('duplicate', command.item)
      }

      // Delete item
      if (command.action == 'delete' && command.item) {
        this.$emit('delete', command.item)
      }

      // Detach from sale
      if (command.action == 'detach' && command.item) {
        this.$emit('detachItemFromSale', command.item)
      }

      // Express edit for prices
      if (command.action == 'editPrices' && command.item) {
        this.$emit('editPrices', command.item)
      }

      // Regrouper des lots sur ce lot
      if (command.action == 'regroup' && command.item) {
        this.$emit('regroup', command.item)
      }
    },
    docIsSigned(item: RequisitionItem) {
      return item.salesMandate?.signed ?? null
    },
    getStructureColor(item: RequisitionItem) {
      return item.requisition?.project?.structure?.color
    },
  },
})
